import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface CommentReaction {
  id: string;
  commentId: string;
  userId: string;
  emoji: string;
}

/** Grouped reaction: emoji + count + list of user IDs */
export interface ReactionGroup {
  emoji: string;
  count: number;
  userIds: string[];
}

interface ReactionRow {
  id: string;
  comment_id: string;
  user_id: string;
  emoji: string;
}

function convert(row: ReactionRow): CommentReaction {
  return {
    id: row.id,
    commentId: row.comment_id,
    userId: row.user_id,
    emoji: row.emoji,
  };
}

/** Fetch all reactions for a set of comment IDs, grouped by comment */
export async function fetchReactionsForComments(
  commentIds: string[]
): Promise<Map<string, ReactionGroup[]>> {
  if (commentIds.length === 0) return new Map();

  const { data, error } = await withRetry(() =>
    supabase
      .from('comment_reactions')
      .select('*')
      .in('comment_id', commentIds)
  );
  if (error) throw error;

  const reactions = (data || []).map(convert);

  // Group by comment_id → emoji
  const map = new Map<string, ReactionGroup[]>();
  for (const r of reactions) {
    if (!map.has(r.commentId)) map.set(r.commentId, []);
    const groups = map.get(r.commentId)!;
    const existing = groups.find(g => g.emoji === r.emoji);
    if (existing) {
      existing.count++;
      existing.userIds.push(r.userId);
    } else {
      groups.push({ emoji: r.emoji, count: 1, userIds: [r.userId] });
    }
  }

  return map;
}

/** Toggle a reaction: add if not present, remove if already reacted */
export async function toggleReaction(
  commentId: string,
  userId: string,
  emoji: string
): Promise<'added' | 'removed'> {
  // Check if already exists
  const { data: existing } = await withRetry(() =>
    supabase
      .from('comment_reactions')
      .select('id')
      .eq('comment_id', commentId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
      .maybeSingle()
  );

  if (existing) {
    await withRetry(() =>
      supabase.from('comment_reactions').delete().eq('id', existing.id)
    );
    return 'removed';
  } else {
    const { error } = await withRetry(() =>
      supabase.from('comment_reactions').insert({
        comment_id: commentId,
        user_id: userId,
        emoji,
      })
    );
    if (error) throw error;
    return 'added';
  }
}
